import { FileDownload, Search } from "@mui/icons-material";
import { Autocomplete, Box, Checkbox, FormControlLabel, Switch, TextField, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import type { DateTime } from "luxon";
import { type FC, type MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditTimePopover } from "../../../components/EditTimePopover";
import { SystemDropdown } from "../../../components/UIToolkit/Inputs/SystemDropDown";
import { DateRangeSelector } from "../../../components/UIToolkit/molecules/DateRangeSelector";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import { NEUTRAL } from "../../../theme/tokens";
import { EmploymentType, type Nurse } from "../../../utils/models";
import { getEmploymentTypeDisplayName } from "../../../utils/utilFunctions";
import { getInitialDateRange } from "../constants";

interface FilterSectionProps {
	startHour: DateTime | null;
	endHour: DateTime | null;
	dateFromFilter: DateTime | null;
	dateToFilter: DateTime | null;
	selectedEmployees: number[];
	selectedLocations: string[];
	selectedContracts: EmploymentType[];
	chargeFilter: boolean | null;
	useActualShifts: boolean;
	availableNurses: Nurse[];
	uniqueLocations: string[];
	isLoading: boolean;
	onStartHourChange: (date: DateTime | null) => void;
	onEndHourChange: (date: DateTime | null) => void;
	onDateFromChange: (date: DateTime | null) => void;
	onDateToChange: (date: DateTime | null) => void;
	onEmployeesChange: (employeeIds: number[]) => void;
	onLocationsChange: (locations: string[]) => void;
	onContractsChange: (contracts: EmploymentType[]) => void;
	onChargeFilterChange: (value: boolean | null) => void;
	onActualShiftsChange: (value: boolean) => void;
	onClearFilters: () => void;
	onRunQuery: () => void;
	onExport: () => void;
	canExport: boolean;
}

export const FilterSection: FC<FilterSectionProps> = ({
	startHour,
	endHour,
	dateFromFilter,
	dateToFilter,
	selectedEmployees,
	selectedLocations,
	selectedContracts,
	chargeFilter,
	useActualShifts,
	availableNurses,
	uniqueLocations,
	isLoading,
	onStartHourChange,
	onEndHourChange,
	onDateFromChange,
	onDateToChange,
	onEmployeesChange,
	onLocationsChange,
	onContractsChange,
	onChargeFilterChange,
	onActualShiftsChange,
	onClearFilters,
	onRunQuery,
	onExport,
	canExport,
}) => {
	const theme = useTheme();
	const { t } = useTranslation();
	const formatPersonName = useFormatPersonName();
	const { from: initialFrom, to: initialTo } = getInitialDateRange();
	const [editTimePopoverAnchor, setEditTimePopoverAnchor] = useState<HTMLElement | null>(null);
	const onEditTimeClick = (e: MouseEvent<HTMLElement>) => {
		setEditTimePopoverAnchor(e.currentTarget);
	};
	const onTimesFiltersChange = ({ newStartTime, newEndTime }: { newStartTime: DateTime; newEndTime: DateTime }) => {
		onStartHourChange(newStartTime);
		onEndHourChange(newEndTime);
	};
	const closeEditTimePopover = () => {
		setEditTimePopoverAnchor(null);
	};

	const hasActiveFilters =
		(dateFromFilter?.diff(initialFrom, "days").days ?? 0) > 0 ||
		(dateToFilter?.diff(initialTo, "days").days ?? 0) > 0 ||
		selectedEmployees.length > 0 ||
		selectedLocations.length > 0 ||
		selectedContracts.length > 0 ||
		chargeFilter !== null ||
		startHour !== null ||
		endHour !== null;
	const availableContracts = Object.values(EmploymentType);

	return (
		<LocalizationProvider dateAdapter={AdapterLuxon}>
			<Box
				data-testid="schedule-filter-section"
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "start",
					width: "100%",
					p: 2,
					gap: 2,
					backgroundColor: NEUTRAL[50],
					borderRadius: "4px",
					flexWrap: "wrap",
				}}
			>
				<DateRangeSelector
					data-testid="schedule-filter-date-range"
					startDate={dateFromFilter ?? undefined}
					endDate={dateToFilter ?? undefined}
					onStartDateChange={onDateFromChange}
					onEndDateChange={onDateToChange}
					padding={0}
					removeBackground
					removeBorder
				/>

				<Autocomplete
					data-testid="schedule-filter-employees"
					multiple
					size="small"
					options={availableNurses}
					getOptionLabel={(option) => formatPersonName(option)}
					value={availableNurses.filter((nurse) => selectedEmployees.includes(nurse.id))}
					onChange={(_, newValue) => onEmployeesChange(newValue.map((nurse) => nurse.id))}
					renderOption={(props, option, { selected }) => (
						<li {...props} key={option.id}>
							<Checkbox checked={selected} />
							{formatPersonName(option)}
						</li>
					)}
					renderInput={(params) => <TextField {...params} label="Employees" placeholder="Select employees" />}
					sx={{ minWidth: 150 }}
				/>

				<Autocomplete
					data-testid="schedule-filter-locations"
					multiple
					size="small"
					options={uniqueLocations}
					value={selectedLocations}
					onChange={(_, newValue) => onLocationsChange(newValue)}
					renderOption={(props, option, { selected }) => (
						<li {...props}>
							<Checkbox checked={selected} />
							{option}
						</li>
					)}
					renderInput={(params) => <TextField {...params} label="Locations" placeholder="Select locations" />}
					sx={{ minWidth: 150 }}
				/>

				<Autocomplete
					data-testid="schedule-filter-contracts"
					multiple
					size="small"
					options={availableContracts}
					getOptionLabel={(option) => getEmploymentTypeDisplayName(option)}
					value={selectedContracts}
					onChange={(_, newValue) => onContractsChange(newValue)}
					renderOption={(props, option, { selected }) => (
						<li {...props} key={option}>
							<Checkbox checked={selected} />
							{getEmploymentTypeDisplayName(option)}
						</li>
					)}
					renderInput={(params) => <TextField {...params} label="Contracts" placeholder="Select contracts" />}
					sx={{ minWidth: 150 }}
				/>

				<SystemDropdown
					data-testid="schedule-filter-charge"
					items={[
						{ value: "all", label: t("charge.all") },
						{ value: "charge", label: t("charge.only") },
						{ value: "non-charge", label: t("charge.non") },
					]}
					selectedItem={chargeFilter === null ? "all" : chargeFilter ? "charge" : "non-charge"}
					onChange={(value) => {
						if (value === undefined) return;
						onChargeFilterChange(value === "all" ? null : value === "charge");
					}}
					formControlSx={{ minWidth: 140 }}
				/>

				<Box
					data-testid="schedule-filter-shift-times"
					onClick={onEditTimeClick}
					sx={{
						display: "flex",
						position: "relative",
						alignItems: "center",
						justifyContent: "center",
						borderRadius: 1,
						border: `1px solid ${NEUTRAL[300]}`,
						height: "40px",
						p: 0.5,
						width: "180px",
						"&:hover": {
							background: alpha(NEUTRAL[300], 0.2),
							cursor: "pointer",
						},
					}}
				>
					<Typography
						sx={{
							position: "absolute",
							left: "10px",
							top: "-10px",
							background: NEUTRAL[50],
							color: theme.palette.primary.main,
							fontSize: "12px",
						}}
					>
						Shift times
					</Typography>
					<Typography>
						{startHour ? startHour.toFormat("h:mm a") : "--:--"} - {endHour ? endHour.toFormat("h:mm a") : "--:--"}
					</Typography>
				</Box>

				<FormControlLabel
					data-testid="schedule-filter-actual-shifts"
					control={
						<Switch
							checked={useActualShifts}
							onChange={(e) => onActualShiftsChange(e.target.checked)}
							size="small"
							sx={{
								"& .MuiSwitch-switchBase.Mui-checked": {
									color: theme.palette.primary.dark,
								},
								"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
									backgroundColor: theme.palette.primary.dark,
								},
							}}
						/>
					}
					label={useActualShifts ? "Actual Shifts" : "Scheduled Shifts"}
					sx={{
						"& .MuiFormControlLabel-label": {
							fontSize: "0.875rem",
							color: theme.palette.primary.dark,
						},
						width: 160,
						m: 0,
					}}
				/>

				<SystemButton
					data-testid="schedule-filter-run-query"
					buttonVariant={"primary"}
					onClick={onRunQuery}
					disabled={!dateFromFilter || !dateToFilter}
					leadingIcon={!isLoading ? <Search /> : undefined}
					loading={isLoading}
					sx={{
						fontSize: {
							xs: "12px",
							xxl: "16px",
						},
						p: 1,
					}}
				>
					{isLoading ? "Searching..." : "Run Query"}
				</SystemButton>

				{hasActiveFilters && (
					<SystemButton
						data-testid="schedule-filter-clear"
						buttonVariant={"secondary"}
						onClick={onClearFilters}
						sx={{
							background: "none",
							fontSize: {
								xs: "14px",
								xxl: "16px",
							},
							p: 1,
						}}
					>
						Clear Filters
					</SystemButton>
				)}

				<SystemButton
					data-testid="schedule-report-export"
					buttonVariant={"secondary"}
					onClick={onExport}
					disabled={!canExport}
					leadingIcon={<FileDownload />}
					sx={{
						background: "none",
						fontSize: {
							xs: "14px",
							xxl: "16px",
						},
						p: 1,
					}}
				>
					Export
				</SystemButton>
				{!!editTimePopoverAnchor && <EditTimePopover onTimeChange={onTimesFiltersChange} onClose={closeEditTimePopover} anchorEl={editTimePopoverAnchor} />}
			</Box>
		</LocalizationProvider>
	);
};
